import {
  getBonuses,
  getBonus,
  newBonus,
  getUserBonuses,
  editBonus,
  publishBonus,
  deleteBonus,
  cancelAssignment,
} from '@services/queries'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import {
  BONUS_CREATION_SUCCESS_MESSAGE,
  BONUS_CREATION_FAILURE_MESSAGE,
  BONUS_EDIT_SUCCESS_MESSAGE,
  BONUS_EDIT_FAILURE_MESSAGE,
  BONUS_PUBLISH_SUCCESS_MESSAGE,
  BONUS_PUBLISH_FAILURE_MESSAGE,
  BONUS_DELETE_SUCCESS_MESSAGE,
  BONUS_DELETE_FAILURE_MESSAGE,
  ASSIGNMENT_CANCEL_SUCCESS_MESSAGE,
  ASSIGNMENT_CANCEL_FAILURE_MESSAGE,
} from '@constants'

export function useBonuses(filters = {}) {
  const { id, type, name, startDate, endDate, pageSize, pageNumber, isDraft } =
    filters

  const info = useQuery(
    [
      'bonuses',
      id,
      type,
      name,
      startDate,
      endDate,
      pageSize,
      pageNumber,
      isDraft,
    ],
    () => getBonuses(filters),
  )

  return info
}

export function useBonus(bonusId) {
  const info = useQuery(['bonus', bonusId], () => getBonus(bonusId))

  return info
}

export function useNewBonusMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (bonus) => {
      return newBonus(bonus.bonus)
    },
    {
      onSuccess(data, bonus) {
        queryClient.invalidateQueries(['bonuses'])
        bonus.setStatusSubmit(true)

        if (onSuccess) onSuccess(data)

        store.addNotification(BONUS_CREATION_SUCCESS_MESSAGE)
      },
      onError(err, bonus) {
        bonus.setStatusSubmit(false)
        console.error('ERROR UPDATING BONUS', err)
        store.addNotification(BONUS_CREATION_FAILURE_MESSAGE)
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}

export function useEditBonusMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  const mutationInfo = useMutation(
    (bonus) => {
      return editBonus(bonus.id, bonus.bonus)
    },
    {
      onSuccess(data, bonus) {
        queryClient.invalidateQueries(['bonuses'])
        queryClient.invalidateQueries(['bonus', bonus.id])

        if (onSuccess) onSuccess(data)

        store.addNotification(BONUS_EDIT_SUCCESS_MESSAGE)
      },
      onError(err) {
        console.error('ERROR UPDATING BONUS', err)
        store.addNotification(BONUS_EDIT_FAILURE_MESSAGE)
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}

export function useUserBonuses(playerId) {
  const info = useQuery(['player_Bonuses', playerId], () =>
    getUserBonuses(playerId),
  )

  return info
}

export function usePublishBonusMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation((id) => publishBonus(id), {
    onSuccess(data, id) {
      queryClient.invalidateQueries(['bonuses'])
      queryClient.invalidateQueries(['bonus', id])
      store.addNotification(BONUS_PUBLISH_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data, id)
    },
    onError(err, id) {
      store.addNotification(BONUS_PUBLISH_FAILURE_MESSAGE)
      if (onError) onError(err, id)
    },
  })
}

export function useDeleteBonusMutation(onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation((id) => deleteBonus(id), {
    onSuccess(data, id) {
      queryClient.invalidateQueries(['bonuses'])
      store.addNotification(BONUS_DELETE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data, id)
    },
    onError(err, id) {
      store.addNotification(BONUS_DELETE_FAILURE_MESSAGE)
      if (onError) onError(err, id)
    },
  })
}

export function useCancelAssignmentMutation(playerId, onSuccess, onError) {
  const queryClient = useQueryClient()
  return useMutation(
    ({ assignmentId, reason }) =>
      cancelAssignment(assignmentId, reason, playerId),
    {
      onSuccess(data, { assignmentId }) {
        queryClient.invalidateQueries(['player_Bonuses', playerId])
        store.addNotification(ASSIGNMENT_CANCEL_SUCCESS_MESSAGE)
        if (onSuccess) onSuccess(data, assignmentId)
      },
      onError(err, { assignmentId }) {
        store.addNotification(ASSIGNMENT_CANCEL_FAILURE_MESSAGE)
        if (onError) onError(err, assignmentId)
      },
    },
  )
}
